import { useEffect } from 'react';

import { Theme } from './Theme';
import { isValidTheme } from './ThemeUtils';
import { useTheme } from './useTheme';

interface IThemeManagerProps {
  readonly defaultTheme?: Theme;
}

const HTML_ATTRIBUTE_THEME = 'data-pmiweb-theme';

function setDocumentElementTheme(theme: Theme) {
  document.documentElement.setAttribute(HTML_ATTRIBUTE_THEME, theme);
}

export function ThemeManager({ defaultTheme }: IThemeManagerProps) {
  const { theme: activeTheme, setTheme } = useTheme();

  useEffect(() => {
    if (defaultTheme && isValidTheme(defaultTheme)) {
      setTheme(defaultTheme);
    }
  }, [defaultTheme, setTheme]);

  useEffect(() => {
    setDocumentElementTheme(activeTheme);
  }, [activeTheme]);

  useEffect(() => {
    const obs = new MutationObserver(record => {
      const event = record[0];
      const isThemeAttributeMutated =
        event &&
        event.type === 'attributes' &&
        event.attributeName === HTML_ATTRIBUTE_THEME;

      if (isThemeAttributeMutated) {
        const currentValue =
          (event.target as HTMLElement).getAttribute(HTML_ATTRIBUTE_THEME) ??
          '';

        // Ensure that, if the html attribute value is changed, we restored it
        // to ensure we are in charge of the current state.
        if (currentValue !== activeTheme) {
          setDocumentElementTheme(activeTheme);
        }
      }
    });

    obs.observe(document.documentElement, {
      attributes: true,
      attributeOldValue: false,
      attributeFilter: [HTML_ATTRIBUTE_THEME],
      subtree: false,
      childList: false,
      characterData: false,
      characterDataOldValue: false,
    });

    return () => {
      obs.disconnect();
    };
  }, [activeTheme]);

  return null;
}
